import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DataTable } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useSmsRecords, useResendSms, useSmsDetail } from '@/hooks/useSms'
import { SmsMessage } from '@/types'
import { Search, MoreHorizontal, Eye, RotateCcw, Download, Filter } from 'lucide-react'
import { format } from 'date-fns'
import { useToast } from '@/hooks/use-toast'

const statusMap: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info' }> = {
  PENDING: { label: '待发送', variant: 'warning' },
  SENT: { label: '已发送', variant: 'info' },
  SUCCESS: { label: '发送成功', variant: 'success' },
  DELIVERED: { label: '已送达', variant: 'success' },
  FAILED: { label: '发送失败', variant: 'destructive' },
}

export default function SmsRecords() {
  const [page, setPage] = useState(0)
  const [filters, setFilters] = useState({
    phone: '',
    status: '',
    startDate: '',
    endDate: '',
  })
  const [selectedRecord, setSelectedRecord] = useState<SmsMessage | null>(null)
  const [showDetailDialog, setShowDetailDialog] = useState(false)

  const { toast } = useToast()
  const { data: recordsData, isLoading } = useSmsRecords({
    page,
    size: 20,
    ...filters
  })

  // 分页信息
  const paginationInfo = recordsData ? {
    page: recordsData.page + 1, // 后端返回从0开始，前端显示从1开始
    totalPages: recordsData.totalPages,
    totalItems: recordsData.total,
    itemsPerPage: 20,
    onPageChange: (newPage: number) => setPage(newPage - 1), // 前端显示从1开始，后端API从0开始
    onItemsPerPageChange: () => {} // 暂时不支持修改每页大小
  } : undefined
  const resendMutation = useResendSms()

  const handleResend = async (record: SmsMessage) => {
    if (confirm(`确定要重新发送给 ${record.mobile} 吗？`)) {
      try {
        await resendMutation.mutateAsync(record.messageId || record.id || '')
        toast({
          title: '重发成功',
          description: `已重新发送给 ${record.mobile}`,
        })
      } catch (error) {
        toast({
          title: '重发失败',
          description: '请稍后重试',
          variant: 'destructive',
        })
      }
    }
  }

  const handleExport = () => {
    // 导出功能实现
    toast({
      title: '导出功能',
      description: '导出功能开发中...',
    })
  }

  const columns: ColumnDef<SmsMessage>[] = [
    {
      accessorKey: 'mobile',
      header: '手机号',
      cell: ({ row }) => {
        const mobile = row.getValue('mobile') as string
        if (!mobile) return '-'
        return mobile.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
      },
    },
    {
      accessorKey: 'content',
      header: '短信内容',
      cell: ({ row }) => {
        const content = row.getValue('content') as string
        return (
          <div className="max-w-xs truncate" title={content}>
            {content}
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: '状态',
      cell: ({ row }) => {
        const statusValue = row.getValue('status') as string
        const status = statusMap[statusValue] || { label: statusValue || '未知', variant: 'outline' as const }
        return <Badge variant={status.variant}>{status.label}</Badge>
      },
    },
    {
      accessorKey: 'cost',
      header: '费用',
      cell: ({ row }) => {
        const cost = row.getValue('cost')
        return cost ? `¥${cost}` : '-'
      },
    },
    {
      accessorKey: 'sentAt',
      header: '发送时间',
      cell: ({ row }) => {
        const sentAt = row.getValue('sentAt') as string
        return sentAt ? format(new Date(sentAt), 'yyyy-MM-dd HH:mm:ss') : '-'
      },
    },
    {
      accessorKey: 'deliveredAt',
      header: '送达时间',
      cell: ({ row }) => {
        const deliveredAt = row.getValue('deliveredAt') as string
        return deliveredAt ? format(new Date(deliveredAt), 'yyyy-MM-dd HH:mm:ss') : '-'
      },
    },
    {
      id: 'actions',
      header: '操作',
      cell: ({ row }) => {
        const record = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setSelectedRecord(record)
                  setShowDetailDialog(true)
                }}
              >
                <Eye className="mr-2 h-4 w-4" />
                查看详情
              </DropdownMenuItem>
              {record.status === 'FAILED' && (
                <DropdownMenuItem onClick={() => handleResend(record)}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  重新发送
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">发送记录</h2>
        <Button onClick={handleExport}>
          <Download className="mr-2 h-4 w-4" />
          导出记录
        </Button>
      </div>

      {/* 筛选条件 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="mr-2 h-4 w-4" />
            筛选条件
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">手机号</Label>
              <Input
                id="phone"
                placeholder="输入手机号"
                value={filters.phone}
                onChange={(e) => setFilters(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>发送状态</Label>
              <Select 
                value={filters.status} 
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择状态" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部状态</SelectItem>
                  <SelectItem value="PENDING">待发送</SelectItem>
                  <SelectItem value="SENT">已发送</SelectItem>
                  <SelectItem value="DELIVERED">已送达</SelectItem>
                  <SelectItem value="FAILED">发送失败</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="startDate">开始日期</Label>
              <Input
                id="startDate"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="endDate">结束日期</Label>
              <Input
                id="endDate"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-4 space-x-2">
            <Button 
              variant="outline" 
              onClick={() => setFilters({ phone: '', status: '', startDate: '', endDate: '' })}
            >
              重置
            </Button>
            <Button>
              <Search className="mr-2 h-4 w-4" />
              查询
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 记录列表 */}
      <Card>
        <CardHeader>
          <CardTitle>发送记录</CardTitle>
          <CardDescription>
            查看所有短信发送记录和状态
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-sm text-muted-foreground">加载中...</div>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={recordsData?.records || []}
              pagination={paginationInfo}
            />
          )}
        </CardContent>
      </Card>

      {/* 详情对话框 */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>短信详情</DialogTitle>
            <DialogDescription>
              查看短信发送的详细信息
            </DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">手机号</Label>
                  <p className="text-sm text-muted-foreground">{selectedRecord.mobile}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">发送状态</Label>
                  <div className="mt-1">
                    <Badge variant={statusMap[selectedRecord.status]?.variant || 'outline'}>
                      {statusMap[selectedRecord.status]?.label || selectedRecord.status || '未知'}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">短信内容</Label>
                <p className="text-sm text-muted-foreground mt-1 p-3 bg-gray-50 rounded-lg">
                  {selectedRecord.content}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">发送时间</Label>
                  <p className="text-sm text-muted-foreground">
                    {selectedRecord.sentAt ? format(new Date(selectedRecord.sentAt), 'yyyy-MM-dd HH:mm:ss') : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium">送达时间</Label>
                  <p className="text-sm text-muted-foreground">
                    {selectedRecord.deliveredAt ? format(new Date(selectedRecord.deliveredAt), 'yyyy-MM-dd HH:mm:ss') : '-'}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">发送费用</Label>
                  <p className="text-sm text-muted-foreground">¥{selectedRecord.cost}</p>
                </div>
                {(selectedRecord.failReason || selectedRecord.errorMessage) && (
                  <div>
                    <Label className="text-sm font-medium">失败原因</Label>
                    <p className="text-sm text-red-600">{selectedRecord.errorMessage || selectedRecord.failReason}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}